import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { hashCode } from 'webapp/utils/number';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import GoalsInsightsAdditionalBoxesForecastComponent from 'webapp/components/goals/insights/additional-boxes/forecast';
import GoalsInsightsAdditionalBoxesPreviousPeriodComponent from 'webapp/components/goals/insights/additional-boxes/previous-period';
import type { GoalInsightsChartState } from 'webapp/components/goals/insights/data-visualization';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';

import type { MetricSetup } from 'webapp/models-custom';
import type DateRange from 'webapp/models-custom/date-range';

import type { ColoredMetricSetupData } from 'webapp/services/metric-details';

interface Args {
	metricSetup: MetricSetup;
	chartState: MetricInsightsChartState | GoalInsightsChartState;
	dateRange: DateRange | null;
	forceLoadingState: boolean;
	origin: string;
}

export default class GoalsInsightsAdditionalBoxesIndexComponent extends Component<Args> {
	@service('metric-details') declare mmMetricExploreService: Service.MetricDetails;
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked forecastMetricSelections: ColoredMetricSetupData[];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const allForecastMetricSelections = this.allForecastMetricSelections;
		this.forecastMetricSelections = allForecastMetricSelections.slice(0, 3);
	}

	get showForecastContent() {
		return this.navigationService.initialRequestsDone && this.billingService.forecastsEnabled;
	}

	get visualization() {
		// we need to cast chartState to MetricInsightsChartState because GoalInsightsChartState doesn't have visualization property and doesn't need it
		return (this.args.chartState as MetricInsightsChartState).visualization;
	}

	get colors() {
		return this.args.chartState.block?.blockSetup.allColorOptions.map((co) => co.hex) || [];
	}

	@cached
	get allForecastMetricSelections(): ColoredMetricSetupData[] {
		const metricSetup = this.args.metricSetup;
		if (metricSetup.isSingleOutput) {
			return metricSetup.metric
				? [
						Object.assign(
							{
								colorIndex: 0,
								attributeName: metricSetup.firstAttributeNameFromBasicFilters,
								unitName: metricSetup.unitName,
							},
							metricSetup.metricSetupWithoutFiltersData,
						),
					]
				: [];
		} else {
			return this.mmMetricExploreService.getSetupsFromMetricInsightsVisualizationData(
				this.visualization,
				metricSetup.metricSetupWithoutFiltersData || undefined,
			);
		}
	}

	get isAnyBoxShown() {
		return (
			this.args.metricSetup.isSingleOutput ||
			(this.showForecastContent && !!this.forecastMetricSelections.length)
		);
	}

	@action
	updateForecasts() {
		const newMetricSelections = this.allForecastMetricSelections.slice(0, 3),
			newHash = hashCode(
				JSON.stringify(
					newMetricSelections.map((ms) => {
						return {
							key: ms.key,
							spaceAccessId: ms.spaceAccessId,
							dimensions: ms.dimensions,
							attributeName: ms.attributeName,
							unitName: ms.unitName,
						};
					}),
				),
			),
			oldHash = hashCode(
				JSON.stringify(
					this.forecastMetricSelections.map((ms) => {
						return {
							key: ms.key,
							spaceAccessId: ms.spaceAccessId,
							dimensions: ms.dimensions,
							attributeName: ms.attributeName,
							unitName: ms.unitName,
						};
					}),
				),
			);

		if (oldHash !== newHash) {
			if (newMetricSelections.length) {
				const baseColorIndex = newMetricSelections[0]!.colorIndex;
				if (newMetricSelections.every((ms) => ms.colorIndex === baseColorIndex)) {
					newMetricSelections.forEach((ms, index) => {
						ms.colorIndex = index;
					});
				}
			}

			const newForecastMetricSelections = newMetricSelections.map((ms) => {
				const newMs: ColoredMetricSetupData = { ...ms };
				newMs.colorHex = this.colors[ms.colorIndex]!;

				return newMs;
			});

			this.forecastMetricSelections = newForecastMetricSelections;
		}
	}
	<template>
		<div
			class={{joinClasses
				(array "insights-additional-boxes-module" (if this.isAnyBoxShown "has-boxes"))
			}}
			{{didUpdate this.updateForecasts this.allForecastMetricSelections}}
		>
			{{#if @metricSetup.isSingleOutput}}
				<GoalsInsightsAdditionalBoxesPreviousPeriodComponent
					@chartState={{@chartState}}
					@metricSetup={{@metricSetup}}
					@dateRange={{@dateRange}}
					@forceLoadingState={{@forceLoadingState}}
				/>
			{{/if}}

			{{#if this.showForecastContent}}
				{{#each this.forecastMetricSelections as |forecastMetricSelection|}}
					<GoalsInsightsAdditionalBoxesForecastComponent
						@metricSetup={{@metricSetup}}
						@coloredMetricSetupData={{forecastMetricSelection}}
						@dateRange={{@dateRange}}
					/>
				{{/each}}
			{{/if}}

		</div>
	</template>
}
